import { supabase } from '../lib/supabase';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// ─── Estimates deal_status values ─────────────────────────────────────────────
// 'Close Won' = closed/won deal
// 'Open'      = open quotation / active estimate
// null/undefined = treat as 'Open'

const isClosedDeal = (r) => (r?.deal_status || '')?.toLowerCase()?.trim() === 'close won';
const isOpenQuotation = (r) => !isClosedDeal(r);

/**
 * Extract financial values from an estimate record.
 * Estimates store financials in the `calculations` JSONB column.
 * For Close Won records, final_project_value / final_target_margin_amount
 * are the authoritative values.
 */
const getSellPrice = (r) => {
  if (isClosedDeal(r)) {
    const finalVal = parseFloat(r?.final_project_value) || 0;
    if (finalVal > 0) return finalVal;
  }
  // Fall back to calculations JSONB
  const calc = r?.calculations || {};
  return parseFloat(calc?.sellPrice) || parseFloat(calc?.sell_price) || 0;
};

const getProfit = (r) => {
  if (isClosedDeal(r)) {
    const finalMargin = parseFloat(r?.final_target_margin_amount) || 0;
    if (finalMargin > 0) return finalMargin;
  }
  const calc = r?.calculations || {};
  return parseFloat(calc?.grossProfit) || parseFloat(calc?.gross_profit) || 0;
};

const getProjectName = (r) => r?.name || r?.project_name || 'Unnamed';
const getClient = (r) => r?.ptc_number || r?.project_name || null;
const getSalesPerson = (r) => r?.won_by || r?.sales_deal_owner_name_snapshot || null;

/**
 * Determine the month number (1-12) for an estimate record.
 * Uses won_date for Close Won records, otherwise created_at.
 */
const getRecordMonth = (r) => {
  const raw = (isClosedDeal(r) && r?.won_date) ? r?.won_date : r?.created_at;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d?.getTime()) ? null : d?.getMonth() + 1;
};

const getRecordYear = (r) => {
  const raw = (isClosedDeal(r) && r?.won_date) ? r?.won_date : r?.created_at;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d?.getTime()) ? null : d?.getFullYear();
};

/**
 * Fetch ALL estimates visible to the current authenticated user.
 * The estimates table uses shared-org RLS (all authenticated users can read all estimates).
 */
const fetchAllEstimates = async () => {
  try {
    const { data, error } = await supabase?.from('estimates')?.select('id, name, project_name, calc_number, ptc_number, deal_status, won_date, won_by, final_project_value, final_target_margin_amount, calculations, config, created_at, updated_at, sales_deal_owner_name_snapshot, sales_deal_owner_department_snapshot, currency');

    if (error) {
      console.error('[ExecutiveSnapshot] fetchAllEstimates error:', error?.message);
      return [];
    }
    console.log('[ExecutiveSnapshot] fetchAllEstimates returned', data?.length, 'records');
    return data || [];
  } catch (err) {
    console.error('[ExecutiveSnapshot] fetchAllEstimates error:', err);
    return [];
  }
};

/**
 * Compute KPI statistics from a list of estimate records
 */
const computeStats = (records) => {
  const all = records || [];
  const closedDeals = all?.filter(isClosedDeal);
  const openQuotations = all?.filter(isOpenQuotation);

  const totalRevenue = closedDeals?.reduce((s, r) => s + getSellPrice(r), 0);
  const totalProfit = closedDeals?.reduce((s, r) => s + getProfit(r), 0);
  const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

  const outstandingPayments = openQuotations?.reduce((s, r) => s + getSellPrice(r), 0);
  const pipelineValue = openQuotations?.reduce((s, r) => s + getSellPrice(r), 0);

  // Project revenue = closed + open (total pipeline)
  const projectRevenue = all?.reduce((s, r) => s + getSellPrice(r), 0);
  const projectProfit = all?.reduce((s, r) => s + getProfit(r), 0);

  return {
    totalProjects: all?.length,
    closedDeals: closedDeals?.length,
    openQuotations: openQuotations?.length,
    activeProjects: openQuotations?.length,
    inProgressProjects: openQuotations?.length,
    delayedProjects: 0,
    completedProjects: closedDeals?.length,
    totalRevenue,
    totalProfit,
    profitMargin,
    outstandingPayments,
    projectRevenue,
    projectProfit,
    pipelineValue,
    closedDealsList: closedDeals,
    allRecordsList: all,
  };
};

/**
 * Fetch monthly snapshot data.
 * Fetches ALL estimates, filters client-side by month+year.
 */
export const fetchMonthlySnapshot = async ({ month, year, department }) => {
  try {
    const allEstimates = await fetchAllEstimates();
    console.log('[ExecutiveSnapshot] Total estimates in DB:', allEstimates?.length);

    // Filter by selected month and year
    const allRecords = allEstimates?.filter(r => {
      const recMonth = getRecordMonth(r);
      const recYear = getRecordYear(r);
      return recMonth === month && recYear === year;
    });

    console.log('[ExecutiveSnapshot] Monthly records for', month, '/', year, ':', allRecords?.length);

    const stats = computeStats(allRecords);
    const { closedDealsList, allRecordsList } = stats;

    // Largest closed deal; fallback to largest estimate overall
    const largestDeal = [...closedDealsList]?.sort((a, b) => getSellPrice(b) - getSellPrice(a))?.[0];
    const largestProject = largestDeal || [...allRecordsList]?.sort((a, b) => getSellPrice(b) - getSellPrice(a))?.[0];

    // Top customer by revenue
    const customerRevMap = {};
    allRecordsList?.forEach(r => {
      const client = getClient(r);
      if (client) {
        customerRevMap[client] = (customerRevMap?.[client] || 0) + getSellPrice(r);
      }
    });
    const topCustomerEntry = Object.entries(customerRevMap)?.sort((a, b) => b?.[1] - a?.[1])?.[0];

    // Top department by revenue (from sales_deal_owner_department_snapshot or config)
    const deptRevMap = {};
    allRecordsList?.forEach(r => {
      const dept = r?.sales_deal_owner_department_snapshot || r?.config?.department || 'General';
      deptRevMap[dept] = (deptRevMap?.[dept] || 0) + getSellPrice(r);
    });
    const topDeptEntry = Object.entries(deptRevMap)?.sort((a, b) => b?.[1] - a?.[1])?.[0];

    // Top sales performer
    const salesMap = {};
    allRecordsList?.forEach(r => {
      const person = getSalesPerson(r);
      if (person) {
        salesMap[person] = (salesMap?.[person] || 0) + getSellPrice(r);
      }
    });
    const topSalesEntry = Object.entries(salesMap)?.sort((a, b) => b?.[1] - a?.[1])?.[0];

    // Previous month comparison
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const prevRecords = allEstimates?.filter(r => {
      const recMonth = getRecordMonth(r);
      const recYear = getRecordYear(r);
      return recMonth === prevMonth && recYear === prevYear;
    });
    const prevStats = computeStats(prevRecords);

    const thisMonthClients = new Set(allRecords.map(r => getClient(r)).filter(Boolean));

    return {
      totalRevenue: stats?.totalRevenue,
      totalProfit: stats?.totalProfit,
      profitMargin: stats?.profitMargin,
      closedDeals: stats?.closedDeals,
      openQuotations: stats?.openQuotations,
      activeProjects: stats?.activeProjects,
      inProgressProjects: stats?.inProgressProjects,
      delayedProjects: stats?.delayedProjects,
      completedProjects: stats?.completedProjects,
      totalProjects: stats?.totalProjects,
      projectRevenue: stats?.projectRevenue,
      projectProfit: stats?.projectProfit,
      pipelineValue: stats?.pipelineValue,
      outstandingPayments: stats?.outstandingPayments,
      newCustomers: thisMonthClients?.size,
      largestDeal: largestProject
        ? { name: getProjectName(largestProject), value: getSellPrice(largestProject), client: getClient(largestProject), status: largestProject?.deal_status }
        : null,
      topCustomer: topCustomerEntry?.[0] || null,
      topCustomerRevenue: topCustomerEntry?.[1] || 0,
      topDepartment: topDeptEntry?.[0] || null,
      topDepartmentRevenue: topDeptEntry?.[1] || 0,
      topSalesPerson: topSalesEntry?.[0] || null,
      topSalesValue: topSalesEntry?.[1] || 0,
      prevRevenue: prevStats?.totalRevenue,
      prevTotalProjects: prevStats?.totalProjects,
      totalRecords: allRecords?.length,
      orgTotalRecords: allEstimates?.length,
    };
  } catch (err) {
    console.error('[ExecutiveSnapshot] fetchMonthlySnapshot error:', err);
    return null;
  }
};

/**
 * Fetch yearly snapshot data.
 * Fetches ALL estimates, filters by year client-side.
 */
export const fetchYearlySnapshot = async ({ year, department }) => {
  try {
    const allEstimates = await fetchAllEstimates();
    console.log('[ExecutiveSnapshot] Total estimates in DB:', allEstimates?.length);

    // Filter to selected year
    const yearRecords = allEstimates?.filter(r => getRecordYear(r) === year);
    console.log('[ExecutiveSnapshot] Estimates for year', year, ':', yearRecords?.length);

    // If no records for this year, show all (handles edge case)
    const allRecords = yearRecords?.length > 0 ? yearRecords : allEstimates;

    const stats = computeStats(allRecords);
    const { closedDealsList, allRecordsList } = stats;

    // Monthly breakdown
    const monthlyMap = {};
    for (let m = 1; m <= 12; m++) monthlyMap[m] = { revenue: 0, profit: 0, deals: 0, quotations: 0, projects: 0 };

    allRecordsList?.forEach(r => {
      let m = getRecordMonth(r);
      if (m && monthlyMap?.[m]) {
        monthlyMap[m].projects += 1;
        if (isClosedDeal(r)) {
          monthlyMap[m].revenue += getSellPrice(r);
          monthlyMap[m].profit += getProfit(r);
          monthlyMap[m].deals += 1;
        } else {
          monthlyMap[m].quotations += 1;
          // Include open estimates in revenue for trend visibility
          monthlyMap[m].revenue += getSellPrice(r);
          monthlyMap[m].profit += getProfit(r);
        }
      }
    });

    const monthlyTrend = Object.entries(monthlyMap)?.map(([m, v]) => ({
      month: parseInt(m),
      revenue: v?.revenue,
      profit: v?.profit,
      deals: v?.deals,
      quotations: v?.quotations,
      projects: v?.projects,
    }));

    const bestMonthEntry = [...monthlyTrend]?.sort((a, b) => b?.revenue - a?.revenue)?.[0];
    const bestMonth = bestMonthEntry?.revenue > 0 ? MONTHS?.[bestMonthEntry?.month - 1] : null;
    const bestMonthRevenue = bestMonthEntry?.revenue || 0;

    const avgMonthlyRevenue = stats?.totalRevenue / 12;
    const avgMonthlyProfit = stats?.totalProfit / 12;

    const uniqueClients = new Set(allRecordsList.map(r => getClient(r)).filter(Boolean));

    const customerRevMap = {};
    allRecordsList?.forEach(r => {
      const client = getClient(r);
      if (client) {
        customerRevMap[client] = (customerRevMap?.[client] || 0) + getSellPrice(r);
      }
    });
    const top5Customers = Object.entries(customerRevMap)?.sort((a, b) => b?.[1] - a?.[1])?.slice(0, 5)?.map(([name, revenue]) => ({ name, revenue }));

    const top5Deals = [...allRecordsList]?.sort((a, b) => getSellPrice(b) - getSellPrice(a))?.slice(0, 5)?.map(r => ({ name: getProjectName(r), value: getSellPrice(r), client: getClient(r), status: r?.deal_status }));

    const deptRevMap = {};
    const deptDealMap = {};
    allRecordsList?.forEach(r => {
      const dept = r?.sales_deal_owner_department_snapshot || r?.config?.department || 'General';
      deptRevMap[dept] = (deptRevMap?.[dept] || 0) + getSellPrice(r);
      deptDealMap[dept] = (deptDealMap?.[dept] || 0) + 1;
    });
    const topDeptEntry = Object.entries(deptRevMap)?.sort((a, b) => b?.[1] - a?.[1])?.[0];

    const highestProfitRecord = [...allRecordsList]?.sort((a, b) => getProfit(b) - getProfit(a))?.[0];

    const topCustomerEntry = Object.entries(customerRevMap)?.sort((a, b) => b?.[1] - a?.[1])?.[0];

    const salesMap = {};
    allRecordsList?.forEach(r => {
      const person = getSalesPerson(r);
      if (person) {
        salesMap[person] = (salesMap?.[person] || 0) + getSellPrice(r);
      }
    });
    const topSalesEntry = Object.entries(salesMap)?.sort((a, b) => b?.[1] - a?.[1])?.[0];

    return {
      totalRevenue: stats?.totalRevenue || stats?.projectRevenue,
      totalProfit: stats?.totalProfit || stats?.projectProfit,
      profitMargin: stats?.profitMargin,
      closedDeals: stats?.closedDeals,
      openQuotations: stats?.openQuotations,
      activeProjects: stats?.activeProjects,
      inProgressProjects: stats?.inProgressProjects,
      delayedProjects: stats?.delayedProjects,
      completedProjects: stats?.completedProjects,
      totalProjects: stats?.totalProjects,
      projectRevenue: stats?.projectRevenue,
      projectProfit: stats?.projectProfit,
      pipelineValue: stats?.pipelineValue,
      activeCustomers: uniqueClients?.size,
      avgMonthlyRevenue,
      avgMonthlyProfit,
      bestMonth,
      bestMonthRevenue,
      topDepartment: topDeptEntry?.[0] || null,
      topDepartmentRevenue: topDeptEntry?.[1] || 0,
      monthlyTrend,
      top5Customers,
      top5Deals,
      bestDepartment: topDeptEntry
        ? { name: topDeptEntry?.[0], revenue: topDeptEntry?.[1], deals: deptDealMap?.[topDeptEntry?.[0]] || 0 }
        : null,
      highestProfitProject: highestProfitRecord
        ? { name: getProjectName(highestProfitRecord), client: getClient(highestProfitRecord), profit: getProfit(highestProfitRecord), revenue: getSellPrice(highestProfitRecord), status: highestProfitRecord?.deal_status }
        : null,
      topCustomer: topCustomerEntry?.[0] || null,
      topCustomerRevenue: topCustomerEntry?.[1] || 0,
      topSalesPerson: topSalesEntry?.[0] || null,
      topSalesValue: topSalesEntry?.[1] || 0,
      orgTotalRecords: allEstimates?.length,
    };
  } catch (err) {
    console.error('[ExecutiveSnapshot] fetchYearlySnapshot error:', err);
    return null;
  }
};

/**
 * Fetch available departments from estimates
 */
export const fetchDepartments = async () => {
  try {
    const { data, error } = await supabase?.from('estimates')?.select('sales_deal_owner_department_snapshot, config');
    if (error) throw error;
    const depts = new Set();
    (data || [])?.forEach(r => {
      const dept = r?.sales_deal_owner_department_snapshot || r?.config?.department;
      if (dept) depts?.add(dept);
    });
    return [...depts]?.sort();
  } catch {
    return [];
  }
};
